import {getLast13MonthsChart, getBackupsDailyChart, getTop10HostBackupChart} from 'util/charts';
import {COLORS_BACKUPS, DEFAULT_VALUE, INIT_INDEX, POS_NEG_LINE} from 'util/constants';
import {invalidateBackupsHosts} from 'modules/store/backupsHosts';
import {invalidateBackupsDaily} from 'modules/store/backupsDaily';
import {invalidateReportingLast2MonthsBackups} from 'modules/store/reportingLast2MonthsBackups';
import {invalidateReportingLast13MonthsBackups} from 'modules/store/reportingLast13MonthsBackups';
import {invalidateReportingBackupsHost} from 'modules/store/reportingBackupsHost';
import {invalidateReportBackups} from 'modules/store/reportBackups';
import {readColors} from 'config/highcharts';

const _getInitialState = () => ({
    top10HostBackupsConfig: null,
    last13MonthsConfig:     null,
    daily:                  {
        selectedHosts: [],
        chartConfig:   {},
        peak:          DEFAULT_VALUE
    }
});

const CREATE_LAST13_MONTHS_CHART_CONFIG = 'CREATE_LAST13_MONTHS_CHART_CONFIG';

/**
 * Create the specified config for the chart of Last 13 Months backup
 * @param seriesName, name of the serie
 * @param backups, array with the data of the serie
 * @param average, string with the name for the field called average
 * @param categories, all categories of the series
 * @param formatter, function to format the average
 * @returns {{type: string, name: *, backups: *, average: *, categories: *}}
 */
export const createLast13MonthsChartConfig = (seriesName, backups, average, categories, formatter) => ({
    type: CREATE_LAST13_MONTHS_CHART_CONFIG,
    name: seriesName,
    backups,
    average,
    categories,
    formatter
});

/**
 Create the necessary serie to set in the chart
 */
const _createAverageData = (categories, average) => categories.map(category =>
    [category[DEFAULT_VALUE], average]
);

/**
 Create the necessary serie for the average
 */
const _getSumTotalSerie = backups => backups.reduce((total, currentBackup) =>
    total + currentBackup.backupSize, DEFAULT_VALUE
);

const _createLast13MonthsChartConfig = (seriesName, backups, average, categories, formatter) => {
    const _createSeriesData = () => backups.map((backup, index) =>
        [categories[index][DEFAULT_VALUE], backup.backupSize]
    );

    const _average = _getSumTotalSerie(backups) / backups.length;
    //const formatterAverage = parseFloat(formatter(_average, 'decimal'));
    const colors = readColors();

    return {
        ...getLast13MonthsChart(formatter),
        series: [{
            name:  seriesName,
            data:  _createSeriesData(categories, backups),
            color: colors[COLORS_BACKUPS.COLORS_BACKUPS_LAST13_BAR]
        }, {
            type:  'spline',
            name:  average,
            data:  _createAverageData(categories, _average),
            color: colors[COLORS_BACKUPS.COLORS_BACKUPS_LAST13_AVG]
        }]
    };
};

const SET_SELECTED_HOSTS = 'SET_SELECTED_HOSTS';

export const setSelectedHosts = (selectedHosts = []) => ({
    type: SET_SELECTED_HOSTS,
    selectedHosts
});

const CREATE_BACKUPS_DAILY_CHART_CONFIG = 'CREATE_BACKUPS_DAILY_CHART_CONFIG';

export const createBackupsDailyChartConfig = (name, data/*, peakName*/, selectedHosts, selectedData, formatter) => ({
    type: CREATE_BACKUPS_DAILY_CHART_CONFIG,
    name, data/*, peakName*/, selectedHosts, selectedData, formatter
});
const _createBackupsDailyChartConfig = (state, {name, data/*, peakName*/, selectedHosts, selectedData, formatter}) => {

    /*
    const _data = data.reduce((ret, {day, size}) => {
        ret.overall.push([day, size]);
        if (size > ret.peak) {
            ret.peak = size;
        }

        return ret;
    }, {
        overall: [],
        peak:    DEFAULT_VALUE
    });
    */
    const _data = data.map(({day, size}) => [day, size]);

    //    const {peak} = _data;

    //    _data.peak = _data.overall.map(([day]) => [day, peak]);
    const colors = readColors();

    const series = [{
        name,
        color: colors[COLORS_BACKUPS.COLORS_BACKUPS_DAILY_OVERALL],
        data:  _data//.overall
    }/*,{
        type:  'spline',
        color: colors[COLORS_BACKUPS.COLORS_BACKUPS_DAILY_PEAK],
        name:  peakName,
        data:  _data.peak
    }*/];

    if (selectedData.length && selectedHosts.length) {
        const
            MAXIMUM_VISIBLE_ELEMENTS = 2,
            showHosts = selectedHosts.slice(INIT_INDEX, MAXIMUM_VISIBLE_ELEMENTS).map(item => item.name),
            diff = selectedHosts.length - showHosts.length;

        let selectedName = showHosts.join(', ');


        if (diff > POS_NEG_LINE) {
            selectedName += ` [+${diff}]`;
        }

        series.push({
            name:  selectedName,
            color: colors[COLORS_BACKUPS.COLORS_BACKUPS_DAILY_HOSTS],
            data:  selectedData.map(({day, size}) => [day, size])
        });
    }

    return {
        ...state.daily,
        //peak,
        chartConfig: {
            ...getBackupsDailyChart(formatter),
            series
        }
    };
};

const CREATE_TOP10HOST_CHART_CONFIG = 'CREATE_TOP10HOST_CHART_CONFIG';

export const createTop10HostChartConfig = (seriesName, backups, formatter) => ({
    type: CREATE_TOP10HOST_CHART_CONFIG,
    name: seriesName,
    backups,
    formatter
});

const _createTop10HostChartConfig = (seriesName, backups, formatter) => {
    const _createSeriesData = () => backups.map(backup =>
        ({
            name: backup.name,
            y:    backup.backupSize
        })
    );
    const colors = readColors();

    return {
        ...getTop10HostBackupChart(formatter),
        series: [
            {
                name:  seriesName,
                data:  _createSeriesData(backups),
                color: colors[COLORS_BACKUPS.COLORS_BACKUPS_TOP10]
            }
        ]
    };
};

export const invalidateBackupsSummary = () => dispatch => {
    dispatch(invalidateBackupsHosts());
    dispatch(invalidateBackupsDaily());
    dispatch(invalidateReportingLast2MonthsBackups());
    dispatch(invalidateReportingLast13MonthsBackups());
    dispatch(invalidateReportingBackupsHost());
};

export const invalidateBackupsReport = () => dispatch => {
    dispatch(invalidateReportBackups());
};

export const reducer = (state = _getInitialState(), action = {}) => {
    switch (action.type) {
    case CREATE_TOP10HOST_CHART_CONFIG:
        return {
            ...state,
            top10HostBackupsConfig: _createTop10HostChartConfig(action.name, action.backups, action.formatter)
        };
    case SET_SELECTED_HOSTS:
        return {
            ...state,
            daily: {
                ...state.daily,
                selectedHosts: action.selectedHosts
            }
        };
    case CREATE_BACKUPS_DAILY_CHART_CONFIG:
        return {
            ...state,
            daily: _createBackupsDailyChartConfig(state, action)
        };
    case CREATE_LAST13_MONTHS_CHART_CONFIG:
        return {
            ...state,
            last13MonthsConfig: _createLast13MonthsChartConfig(
                action.name,
                action.backups,
                action.average,
                action.categories,
                action.formatter
            )
        };
    default:
        return state;
    }
};



// WEBPACK FOOTER //
// ./src/modules/backups.js